import DateUtils from '@/utils/DateUtils';
import SvgUtils from '@/utils/pdfGenerator/SvgUtils';
import SvgChart, { SVGRenderer } from '@wuba/react-native-echarts/svgChart';
import { BarChart, LineChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  ToolboxComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutChangeEvent, Platform, View } from 'react-native';
import SimpleLink from '../SimpleLink';

// Register extensions
echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  SVGRenderer,
  BarChart,
  LineChart,
  LegendComponent,
  ToolboxComponent,
]);

const E_HEIGHT = 400;

type StatsBarChartProps = {
  data?: (
    | [string, number, number, number]
    | [string, number, number, number, number]
    | [string, number]
    | [string, number, number]
  )[];
  type?: 'bar' | 'line';
};

const StatsBarChart = (props: StatsBarChartProps) => {
  const { data, type = 'line' } = props;

  const chartRef = useRef<any>(null);
  const svgRef = useRef<any>(null);
  const [width, setWidth] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.resize({
        width: width,
        height: E_HEIGHT,
      });
    }
  }, [width]);

  useEffect(() => {
    let chart: any;
    if (svgRef.current) {
      const option = {
        animation: true,
        tooltip: {
          trigger: 'axis',
        },
        legend: {
          show: true,
          top: '3%',
        },
        xAxis: {
          type: 'category',
          smooth: true,
          data: data?.map((v) => v[0]),
        },
        yAxis: [
          {
            type: 'value',
          },
          {
            type: 'value',
          },
        ],
        series: [
          {
            name: t('Nb patients'),
            data: data?.map((v) => v[1]),
            type,
            label: {
              show: true,
              position: 'top',
              formatter: function (params) {
                // Calculer le total
                const total =
                  data?.map((v) => v[1]).reduce((sum, current) => sum + current, 0) || 0;
                // Calculer le pourcentage
                const percent = (total > 0 ? (params.value / total) * 100 : 0).toFixed(1);
                // Retourner le pourcentage formaté
                return percent + '%';
              },
            },
          },
          {
            name: t("Nb d'hommes"),
            data: data?.map((v) => v[2]),
            type,
            label: {
              show: true,
              position: 'top',
              formatter: function (params) {
                // Calculer le total
                const total =
                  data?.map((v) => v[2]).reduce((sum, current) => (sum || 0) + (current || 0), 0) ||
                  0;
                // Calculer le pourcentage
                const percent = (total > 0 ? (params.value / total) * 100 : 0).toFixed(1);
                // Retourner le pourcentage formaté
                return percent + '%';
              },
            },
          },
          {
            name: t('Nb de femmes'),
            data: data?.map((v) => v[3]),
            type,
            label: {
              show: true,
              position: 'top',
              formatter: function (params) {
                // Calculer le total
                const total =
                  data?.map((v) => v[3]).reduce((sum, current) => (sum || 0) + (current || 0), 0) ||
                  0;
                // Calculer le pourcentage
                const percent = (total > 0 ? (params.value / total) * 100 : 0).toFixed(1);
                // Retourner le pourcentage formaté
                return percent + '%';
              },
            },
          },
          {
            name: t('Nb non précisé'),
            data: data?.map((v) => v[4]),
            type,
            label: {
              show: true,
              position: 'top',
              formatter: function (params) {
                // Calculer le total
                const total =
                  data?.map((v) => v[4]).reduce((sum, current) => (sum || 0) + (current || 0), 0) ||
                  0;
                // Calculer le pourcentage
                const percent = (total > 0 ? (params.value / total) * 100 : 0).toFixed(1);
                // Retourner le pourcentage formaté
                return percent + '%';
              },
            },
          },
        ],
      };

      // @ts-ignore
      if (!chartRef.current) {
        chart = echarts.init(svgRef.current, 'light', {
          renderer: 'svg',
          width: width,
          height: E_HEIGHT,
        });
        chartRef.current = chart;
      }

      chartRef.current.setOption(option);
    }
  }, [data, width]);

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
  }, []);

  const handleLayout = (e: LayoutChangeEvent) => {
    const { width } = e.nativeEvent.layout;
    setWidth(width);
  };

  return (
    <View onLayout={handleLayout} style={{ flex: 1, flexShrink: 0, flexBasis: 'auto' }}>
      <SvgChart ref={svgRef} />
      {Platform.OS === 'web' ? (
        <SimpleLink
          onPress={async () => {
            await SvgUtils.downloadPngFromSVG(
              chartRef.current?.getSvgDataURL(),
              width,
              Math.max(E_HEIGHT, width * 0.25),
              { x: 0, y: 0, width, height: Math.max(E_HEIGHT, width * 0.25), color: '#ffffff' },
              true,
              t('Repartition_Age') + '_' + DateUtils.formatDate(new Date()) + '.png'
            );
          }}
        >
          Télécharger
        </SimpleLink>
      ) : null}
    </View>
  );
};

export default StatsBarChart;
